import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { from, Observable,lastValueFrom, catchError, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { AuthService, TOKEN_KEY } from '../services/auth.service';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

  type: 'iOS' | 'Android' | 'Web' = this.platform.is('capacitor') ? this.platform.is('android') ? 'Android' : 'iOS' : 'Web';

  private version?: string;

  constructor(private authService: AuthService, private router: Router, private platform: Platform,) {

  }

  // The backend reads this to decide whether the client is too old, and keeps a separate
  // minimum per platform. On native, environment.version is the version of the web bundle
  // the binary was built from - not the version the store shows - so report what the store
  // knows instead. Resolved once and kept, since it cannot change while the app is running.
  private async getVersion(): Promise<string> {
    if (this.version) {
      return this.version;
    }

    if (Capacitor.isNativePlatform()) {
      try {
        // .version is versionName on Android / CFBundleShortVersionString on iOS, i.e. the
        // '3.0.3' the store displays. Not .build, which is the versionCode ('65').
        this.version = (await App.getInfo()).version;
      } catch (error) {
        console.log('could not read app info, falling back to bundle version');
        this.version = environment.version;
      }
    } else {
      this.version = environment.version;
    }

    return this.version;
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isOtpAccessUrl = request.url.includes('/autho/otp/access/');
    const isTokenUrl = /\/token\/?$/.test(request.url);
    // The revoke call answers 401 token_revoked on a token that is already gone, and it is
    // answered after AuthService has moved on. Left to the handler below, that late 401 would
    // log out whoever is signed in by then - possibly a session started after this one ended.
    const isLogoutUrl = request.url.includes('/autho/logout/');
    return from(this.handleAccess(request, next)).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          if (isOtpAccessUrl || isTokenUrl || isLogoutUrl) {
            return throwError(() => error);
          }
          // Handle 401 error here (e.g., redirect to login page or perform any desired action)
          // You can also throw an error to propagate it to the caller
          // throw new Error('Unauthorized');
          console.log('ERROR AUTH')
          this.authService.logout();
          this.router.navigateByUrl('/authentication/login', { replaceUrl: true });
        }
        return throwError(() => error);
      })
    );
  }

  private async handleAccess(request: HttpRequest<any>, next: HttpHandler):Promise<HttpEvent<any>> {
    let token;

    try {
      token = await SecureStoragePlugin.get({ key: TOKEN_KEY });
    } catch (error) {
      console.log('no token in storage');
    }
    const isApiUrl = request.url.startsWith(environment.restApiUrl);
    const isTokenUrl = /\/token\/?$/.test(request.url);
    const version = await this.getVersion();

    if (token && isApiUrl && !isTokenUrl) {
        request = request.clone({
            setHeaders: {
                Authorization: `Bearer ${token.value}`,
                'x-platform': this.type + ' (' + version + ')'
            }
        });
    }
    else if(isApiUrl) {
      request = request.clone({
        setHeaders: {
            'x-platform': this.type + ' (' + version + ')'
        }
      });
    }

    return lastValueFrom(next.handle(request));
  }
}
